import {
  OutputFormStatus,
  QualityCheckResult,
  SectionStatus,
} from '../types';
import { api, getErrorMessage, handleServiceError } from './api';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { roadmapService } from './roadmapService';

export const outputFormService = {
  // Buscar status do formulário de saída - usa roadmaps reais da proposta
  getStatus: async (proposalId: number): Promise<OutputFormStatus> => {
    try {
      // Buscar roadmaps reais da proposta
      const roadmaps = await roadmapService.getByProposal(proposalId);
      
      if (roadmaps.length === 0) {
        // Retornar estrutura vazia se não houver roadmaps
        return {
          project_type: 'Edital Nacional',
          required_complete: 0,
          required_total: 0,
          optional_included: 0,
          optional_total: 0,
          quality_score: 0,
          overall_status: 'empty',
          sections: [],
        };
      }

      // Calcular métricas baseadas nos roadmaps reais
      const requiredSections = roadmaps.filter(r => r.is_required !== false);
      const optionalSections = roadmaps.filter(r => r.is_required === false);
      
      const requiredComplete = requiredSections.filter(r => r.content && r.content.trim().length > 50).length;
      const optionalIncluded = optionalSections.filter(r => r.content && r.content.trim().length > 0).length;
      
      // Calcular score de qualidade
      const qualityScore = requiredSections.length > 0 
        ? Math.round((requiredComplete / requiredSections.length) * 100)
        : 0;

      // Mapear roadmaps para SectionStatus
      const sections: SectionStatus[] = roadmaps.map(roadmap => {
        const wordCount = roadmap.content ? roadmap.content.trim().split(/\s+/).length : 0;
        const hasContent = wordCount > 0;
        const isValid = hasContent && (!roadmap.character_limit || wordCount >= 50);
        
        return {
          name: roadmap.topic,
          roadmap_item_id: roadmap.id, // ✅ ID REAL do backend
          is_required: roadmap.is_required !== false,
          is_included: hasContent,
          validation_status: isValid ? 'valid' : hasContent ? 'warning' : 'empty',
          content: roadmap.content || '',
          word_count: wordCount,
          word_limit: roadmap.character_limit || undefined,
          last_edited: roadmap.updated_at,
          validation_messages: [],
        };
      });

      return {
        project_type: 'Edital Nacional', // TODO: Obter do backend
        required_complete: requiredComplete,
        required_total: requiredSections.length,
        optional_included: optionalIncluded,
        optional_total: optionalSections.length,
        quality_score: qualityScore,
        overall_status: qualityScore >= 80 ? 'ready' : qualityScore >= 50 ? 'incomplete' : 'empty',
        sections,
      };
    } catch (error) {
      console.error('[outputFormService] Erro ao buscar status:', error);
      handleServiceError(error, { feature: 'output_form', action: 'unknown' });
    }
  },

  // Executar checklist de qualidade - Simplificado (mock por enquanto)
  runQualityCheck: async (proposalId: number, projectType: string): Promise<QualityCheckResult> => {
    // TODO: Implementar endpoint real no backend
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      overall_score: 75,
      checks: {
        completeness: { score: 80, issues: [] },
        formatting: { score: 70, issues: [] },
        consistency: { score: 75, issues: [] },
        compliance: { score: 75, issues: [] },
      },
      suggested_actions: ['Verificar formatação das seções'],
    };
  },

  // Formatar conteúdo de seção - Todas as formatações disponíveis
  formatContent: async (
    roadmapItemId: number,
    content: string,
    action: string
  ): Promise<string> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const formatters: Record<string, (text: string) => string> = {
      format_lists: (text) => {
        return text.replace(/^-\s/gm, '• ').replace(/^\d+\.\s/gm, (match, offset) => {
          const lineNum = text.substring(0, offset).split('\n').length;
          return `${lineNum}. `;
        });
      },
      format_tables: (text) => {
        if (text.includes('|')) {
          return text;
        }
        return `${text}\n\n| Coluna 1 | Coluna 2 | Coluna 3 |\n|----------|----------|----------|\n| Dado 1   | Dado 2   | Dado 3   |`;
      },
      format_headings: (text) => {
        return text.replace(/^(.+)$/gm, (match) => {
          if (match.startsWith('#')) return match;
          if (match.length > 50) return match;
          if (match.trim() === '') return match;
          return `## ${match}`;
        });
      },
      format_paragraphs: (text) => text.replace(/\n{3,}/g, '\n\n').trim(),
      format_citations: (text) => text.replace(/\(([^)]+), \d{4}\)/g, '($1, **$2**)'),
      format_numbers: (text) => text.replace(/\b(\d{4,})\b/g, (match) => {
        return parseInt(match).toLocaleString('pt-BR');
      }),
    };
    
    const formatter = formatters[action];
    return formatter ? formatter(content) : content;
  },

  // Toggle inclusão de seção - Não aplica no roadmap real (é virtual)
  toggleInclusion: async (roadmapItemId: number, isIncluded: boolean): Promise<boolean> => {
    // Esta funcionalidade é virtual no frontend, não persiste no backend
    await new Promise(resolve => setTimeout(resolve, 100));
    return true;
  },

  // Adicionar seções ao roteiro - Chama API real
  addSections: async (proposalId: number, sectionNames: string[]): Promise<number> => {
    try {
      let createdCount = 0;
      
      // Buscar roadmaps existentes para determinar posição
      const existingRoadmaps = await roadmapService.getByProposal(proposalId);
      const startPosition = existingRoadmaps.length + 1;
      
      // Criar cada seção como um roadmap item
      for (let i = 0; i < sectionNames.length; i++) {
        try {
          await roadmapService.create(
            proposalId,
            sectionNames[i],
            `Descreva o conteúdo da seção "${sectionNames[i]}"...`,
            startPosition + i
          );
          createdCount++;
        } catch (error) {
          console.error(`[outputFormService] Erro ao criar seção ${sectionNames[i]}:`, error);
        }
      }
      
      return createdCount;
    } catch (error) {
      console.error('[outputFormService] Erro ao adicionar seções:', error);
      handleServiceError(error, { feature: 'output_form', action: 'add_sections' });
    }
  },

  // Buscar preview paginado - TODO: Implementar endpoint real
  getPreview: async (proposalId: number): Promise<{
    total_pages: number;
    pages: Array<{ page_number: number; content: string }>;
  }> => {
    try {
      // Buscar roadmaps reais para gerar preview
      const roadmaps = await roadmapService.getByProposal(proposalId);
      
      if (roadmaps.length === 0) {
        return {
          total_pages: 1,
          pages: [{ page_number: 1, content: '# Proposta vazia\n\nNenhuma seção criada.' }],
        };
      }
      
      // Gerar conteúdo do preview a partir dos roadmaps
      const content = roadmaps
        .filter(r => r.content && r.content.trim())
        .map(r => `# ${r.topic}\n\n${r.content}`)
        .join('\n\n---\n\n');
      
      return {
        total_pages: 1,
        pages: [{ page_number: 1, content: content || '# Proposta sem conteúdo' }],
      };
    } catch (error) {
      console.error('[outputFormService] Erro ao gerar preview:', error);
      handleServiceError(error, { feature: 'output_form', action: 'get_preview' });
    }
  },

  // Analisar documentos - TODO: Implementar endpoint real
  analyzeDocuments: async (proposalId: number): Promise<{
    score: number;
    analysis: string;
    recommendations: string[];
  }> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      score: 75,
      analysis: 'Análise dos documentos da proposta.',
      recommendations: ['Verificar documentação complementar'],
    };
  },
};

// Regras de seções por tipo de projeto
export const SECTION_RULES: Record<string, { required: string[]; optional: string[] }> = {
  'Edital Nacional': {
    required: ['Introdução', 'Objetivos', 'Metodologia', 'Resultados Esperados', 'Cronograma', 'Orçamento', 'Equipe', 'Justificativa'],
    optional: ['Referências Bibliográficas', 'Anexos', 'Impacto Social', 'Parcerias', 'Sustentabilidade', 'Infraestrutura', 'Histórico da Instituição'],
  },
  'Edital Estadual': {
    required: ['Introdução', 'Objetivos', 'Metodologia', 'Justificativa', 'Resultados'],
    optional: ['Cronograma', 'Referências', 'Anexos', 'Orçamento', 'Parcerias'],
  },
  'Projeto Interno': {
    required: ['Introdução', 'Objetivos', 'Justificativa'],
    optional: ['Metodologia', 'Cronograma', 'Orçamento', 'Equipe', 'Resultados', 'Referências'],
  },
  'Outro': {
    required: ['Introdução', 'Objetivos'],
    optional: ['Metodologia', 'Justificativa', 'Resultados', 'Cronograma', 'Orçamento', 'Equipe', 'Referências', 'Anexos'],
  },
};

// Descrições das seções disponíveis
export const AVAILABLE_SECTIONS: Record<string, string> = {
  'Introdução': 'Contextualização e apresentação do projeto',
  'Objetivos': 'Objetivos gerais e específicos do projeto',
  'Metodologia': 'Abordagem metodológica e procedimentos',
  'Justificativa': 'Relevância e necessidade do projeto',
  'Resultados Esperados': 'Resultados e produtos a serem alcançados',
  'Cronograma': 'Timeline do projeto com marcos e entregas',
  'Orçamento': 'Detalhamento financeiro do projeto',
  'Equipe': 'Composição e qualificação da equipe',
  'Referências Bibliográficas': 'Lista de referências citadas no documento',
  'Anexos': 'Documentos complementares e material de apoio',
  'Impacto Social': 'Impactos sociais e benefícios à comunidade',
  'Parcerias': 'Instituições parceiras e colaborações',
  'Sustentabilidade': 'Sustentabilidade financeira e ambiental',
  'Infraestrutura': 'Infraestrutura disponível para o projeto',
  'Histórico da Instituição': 'Histórico e capacidade institucional',
  'Resultados': 'Resultados esperados e indicadores',
};
